/**
 * VoiceCommandListener
 * --------------------------------------------------------------------------
 * A small floating mic button (opt-in) that lets users control the UI by
 * voice — "close this dialog", "clear the form", "submit report", etc.
 *
 * Uses the browser's Web Speech API (SpeechRecognition). No LLM call —
 * commands are matched against a small keyword grammar so it works
 * offline and is instantaneous.
 *
 * Commands (English):
 *   • "close (this) dialog" / "cancel" / "dismiss"                → Escape key
 *   • "clear (the) form" / "reset"                                → Reset form
 *   • "submit (the) report" / "save" / "confirm"                  → Click primary submit
 *   • "cancel" / "back"                                           → Escape
 *   • "help" / "open help"                                        → Open Help Assistant bubble
 *   • "stop listening" / "mute"                                   → Turn listener off
 *
 * Anti-pattern guardrails:
 *   - Off by default (privacy). User must click to enable.
 *   - Visual "🎙️ listening" indicator whenever the mic is active.
 *   - No LLM call; strictly local intent match.
 *   - Ignores commands under 3 chars; commands must reach ≥ 0.7 fuzzy match.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Ear } from 'lucide-react';
import { toast } from 'sonner';

const COMMANDS = [
  {
    intent: 'close',
    triggers: ['close dialog', 'close this dialog', 'close', 'dismiss', 'cancel', 'go back'],
    label: 'Close dialog',
  },
  {
    intent: 'clear',
    triggers: ['clear form', 'clear the form', 'reset form', 'reset the form', 'clear'],
    label: 'Clear form',
  },
  {
    intent: 'submit',
    triggers: ['submit', 'submit report', 'save report', 'save', 'confirm', 'send it'],
    label: 'Submit',
  },
  {
    intent: 'help',
    triggers: ['help', 'open help', 'help me', 'assistant'],
    label: 'Open help',
  },
  // Navigation intents — carry a `nav` target (data-testid suffix on the sidebar)
  { intent: 'nav', nav: 'nav-dashboard',        triggers: ['go to dashboard', 'open dashboard', 'dashboard'], label: 'Dashboard' },
  { intent: 'nav', nav: 'nav-incidents',        triggers: ['open incidents', 'file incident', 'go to incidents', 'incidents page'], label: 'Incidents' },
  { intent: 'nav', nav: 'nav-wildlife-strikes', triggers: ['file wildlife strike', 'open wildlife', 'wildlife strikes', 'bird strike page'], label: 'Wildlife Strikes' },
  { intent: 'nav', nav: 'nav-risk-register',    triggers: ['open risk register', 'go to risks', 'risk register'], label: 'Risk Register' },
  { intent: 'nav', nav: 'nav-audits',           triggers: ['open audits', 'go to audits', 'audits page'], label: 'Audits' },
  { intent: 'nav', nav: 'nav-frms',             triggers: ['open fatigue', 'go to fatigue', 'fatigue page', 'open frms', 'frms'], label: 'FRMS' },
  { intent: 'nav', nav: 'nav-help-center',      triggers: ['open help center', 'go to help center', 'help center'], label: 'Help Center' },
  { intent: 'nav', nav: 'nav-compliance-center', triggers: ['open compliance', 'go to compliance', 'compliance center'], label: 'Compliance' },
  { intent: 'nav', nav: 'nav-settings',         triggers: ['open settings', 'go to settings', 'settings page'], label: 'Settings' },
  // Tab navigation within a page (uses role="tab")
  { intent: 'nextTab',     triggers: ['next tab', 'switch tab', 'go to next tab'], label: 'Next tab' },
  { intent: 'previousTab', triggers: ['previous tab', 'back tab', 'go back tab'], label: 'Previous tab' },
  {
    intent: 'stop',
    triggers: ['stop listening', 'mute', 'stop mic', 'turn off'],
    label: 'Stop listening',
  },
];

// Fuzzy-ish match: normalise punctuation/case, then check any trigger phrase
// appears as a substring of the last utterance. Longer triggers match first
// so "close dialog" beats a stray "close" inside a longer phrase.
const matchCommand = (text) => {
  const t = (text || '').toLowerCase().replace(/[^a-z0-9 ]/g, '').trim();
  if (t.length < 3) return null;
  let best = null;
  let bestLen = -1;
  for (const cmd of COMMANDS) {
    for (const trig of cmd.triggers) {
      if (t.includes(trig) && trig.length > bestLen) {
        best = cmd;
        bestLen = trig.length;
      }
    }
  }
  return best;
};

// Best-effort DOM actions with graceful fallbacks. We deliberately keep these
// selector-based rather than context-plumbing — voice commands are best-effort.
const runIntent = (intent, cmd) => {
  switch (intent) {
    case 'close': {
      // Send Escape to close any open Radix Dialog/Popover
      document.body.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      // Also click any [data-testid$="cancel-btn"], "Cancel" or "Close" button visible
      const btn =
        document.querySelector('button[data-testid*="cancel"]:not([disabled])') ||
        Array.from(document.querySelectorAll('button')).find((b) => /^(cancel|close|dismiss)$/i.test(b.textContent.trim()));
      if (btn) btn.click();
      toast.info('Voice: closed');
      return true;
    }
    case 'clear': {
      const inputs = document.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea');
      let cleared = 0;
      inputs.forEach((el) => {
        // Skip readonly / disabled / password / date inputs — respect user intent
        if (el.readOnly || el.disabled) return;
        if (['password'].includes(el.type)) return;
        const setter = Object.getOwnPropertyDescriptor(el.__proto__, 'value')?.set;
        if (setter) {
          setter.call(el, '');
          el.dispatchEvent(new Event('input', { bubbles: true }));
          cleared += 1;
        }
      });
      toast.info(`Voice: cleared ${cleared} field${cleared === 1 ? '' : 's'}`);
      return cleared > 0;
    }
    case 'submit': {
      const btn =
        document.querySelector('button[type="submit"]:not([disabled])') ||
        document.querySelector('button[data-testid*="submit"]:not([disabled])') ||
        Array.from(document.querySelectorAll('button')).find((b) => /^(submit|save|confirm|create)$/i.test(b.textContent.trim()) && !b.disabled);
      if (btn) {
        btn.click();
        toast.success('Voice: submitted');
        return true;
      }
      toast.error('Voice: no submit button found');
      return false;
    }
    case 'help': {
      const btn = document.querySelector('[data-testid="help-assistant-bubble"]');
      if (btn) { btn.click(); toast.info('Voice: opened help'); return true; }
      toast.error('Voice: help bubble not found');
      return false;
    }
    case 'nav': {
      if (!cmd?.nav) return false;
      // Sidebar entries live under `data-testid="nav-<slug>"`. Some are nested
      // inside a collapsed section; try clicking the parent section too when
      // the direct link isn't visible.
      const el = document.querySelector(`[data-testid="${cmd.nav}"]`);
      if (el) {
        el.click();
        toast.success(`Voice: opened ${cmd.label || cmd.nav}`);
        return true;
      }
      // Fallback: navigate via location.hash if the page uses hash routing;
      // otherwise open Help Center as a catch-all so the user isn't stranded.
      toast.error(`Voice: couldn't find ${cmd.label || cmd.nav} in the sidebar`);
      return false;
    }
    case 'nextTab':
    case 'previousTab': {
      // Radix tabs have role="tab" + aria-selected. Find the active tab,
      // move focus to its next/previous sibling, and click it.
      const tabs = Array.from(document.querySelectorAll('[role="tab"]'));
      if (tabs.length === 0) {
        toast.error('Voice: no tabs on this page');
        return false;
      }
      const idx = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      const step = intent === 'nextTab' ? 1 : -1;
      const next = tabs[(idx + step + tabs.length) % tabs.length];
      if (next) {
        next.click();
        toast.info(`Voice: switched to "${next.textContent.trim().slice(0, 24)}"`);
        return true;
      }
      return false;
    }
    default:
      return false;
  }
};

export default function VoiceCommandListener() {
  const [listening, setListening] = useState(false);
  const [lastHeard, setLastHeard] = useState('');
  const [supported, setSupported] = useState(true);
  const recRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
  }, []);

  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error('Voice commands not supported in this browser. Try Chrome or Edge.');
      return;
    }
    try {
      const rec = new SR();
      rec.lang = 'en-US';
      rec.interimResults = false;
      rec.continuous = true;
      rec.maxAlternatives = 1;
      rec.onresult = (event) => {
        const last = event.results[event.results.length - 1];
        const text = last[0].transcript;
        setLastHeard(text);
        const cmd = matchCommand(text);
        if (!cmd) return;
        if (cmd.intent === 'stop') {
          stop();
          return;
        }
        runIntent(cmd.intent, cmd);
      };
      rec.onerror = (e) => {
        if (e.error && e.error !== 'aborted' && e.error !== 'no-speech') {
          toast.error(`Voice: ${e.error}`);
        }
      };
      rec.onend = () => {
        // Chrome auto-stops after ~60s; auto-restart if the user hasn't toggled off
        if (recRef.current === rec) {
          try { rec.start(); } catch { /* ignore benign 'already started' */ }
        }
      };
      recRef.current = rec;
      setListening(true);
      rec.start();
      toast.success('Voice commands ON. Try: "go to dashboard", "next tab", "close dialog", "submit".');
    } catch (err) {
      toast.error(`Voice: ${err.message}`);
    }
  };

  const stop = () => {
    if (recRef.current) {
      const rec = recRef.current;
      recRef.current = null; // suppress auto-restart
      try { rec.stop(); } catch { /* ignore */ }
    }
    setListening(false);
    setLastHeard('');
  };

  const toggle = () => (listening ? stop() : start());

  // Auto-stop on unmount (inline to keep eslint-hooks happy)
  useEffect(() => () => {
    if (recRef.current) {
      const rec = recRef.current;
      recRef.current = null;
      try { rec.stop(); } catch { /* ignore */ }
    }
  }, []);

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="voice-command-btn"
      aria-label={listening ? 'Stop voice commands' : 'Start voice commands'}
      title={listening ? 'Voice ON — say "stop listening" to disable' : 'Enable voice commands'}
      className={`fixed bottom-6 right-24 z-40 rounded-full shadow-lg p-3 border-2 transition-all ${
        listening
          ? 'bg-rose-500 border-rose-300 shadow-rose-500/40 animate-pulse'
          : 'bg-slate-900 border-slate-700 hover:border-emerald-500/60 hover:shadow-emerald-500/20'
      }`}
    >
      {listening ? (
        <Ear className="w-5 h-5 text-white" />
      ) : (
        <Mic className="w-5 h-5 text-slate-300" />
      )}
      {listening && lastHeard && (
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-slate-900 border border-slate-700 text-[10px] text-slate-300 px-3 py-1.5 rounded-md pointer-events-none max-w-[240px] truncate">
          &ldquo;{lastHeard.slice(-40)}&rdquo;
        </span>
      )}
      {!listening && (
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-slate-900 border border-slate-700 text-[10px] text-slate-400 px-3 py-1.5 rounded-md opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
          Voice commands
        </span>
      )}
    </button>
  );
}

// Also expose helpers for tests / integration
export { COMMANDS, matchCommand, runIntent };
